import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Plus, FileText, LogOut, Loader2 } from 'lucide-react';
import api from '../lib/api';
import { removeToken, getUser } from '../lib/auth';
import { getStatusColor, formatDate } from '../lib/utils';

export default function DashboardPage() {
  const navigate = useNavigate();
  const user = getUser();
  const [loading, setLoading] = useState(true);
  const [applications, setApplications] = useState([]);

  useEffect(() => {
    fetchApplications();
  }, []);

  const fetchApplications = async () => {
    try {
      const response = await api.get('/api/applications/me');
      setApplications(response.data);
    } catch (error) {
      toast.error('Failed to load applications');
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    removeToken();
    toast.success('Logged out successfully');
    navigate('/');
  };

  return (
    <div className="min-h-screen bg-white/40" data-testid="dashboard-container">
      {/* Header */}
      <header className="bg-white border-b border-white/40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex justify-between items-center">
            <div className="flex items-center space-x-2">
              <div className="w-10 h-10 bg-gradient-to-br from-teal-500 to-teal-600 rounded-xl flex items-center justify-center">
                <span className="text-white font-bold text-xl">G</span>
              </div>
              <div>
                <h1 className="text-xl font-bold text-navy-900">girgit</h1>
                <p className="text-xs text-neutral-600">Dashboard</p>
              </div>
            </div>
            <div className="flex items-center gap-4">
              <span className="text-sm text-neutral-600">
                Welcome, <span className="font-semibold text-navy-800">{user?.full_name}</span>
              </span>
              <button
                onClick={handleLogout}
                className="flex items-center gap-2 text-neutral-700 hover:text-navy-900 transition-colors"
                data-testid="logout-button"
              >
                <LogOut className="w-5 h-5" />
                Logout
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto py-12 px-4 sm:px-6 lg:px-8">
        {/* Header Section */}
        <div className="flex justify-between items-center mb-8">
          <div>
            <h1 className="text-4xl font-bold text-navy-900 mb-2">
              My Applications
            </h1>
            <p className="text-lg text-neutral-600">
              Track and manage your career applications
            </p>
          </div>
          <Link
            to="/roles"
            className="inline-flex items-center px-6 py-3 bg-teal-500 text-white rounded-xl hover:bg-teal-600 transition-all duration-200 shadow-soft hover:shadow-soft font-medium"
            data-testid="new-application-button"
          >
            <Plus className="w-5 h-5 mr-2" />
            Browse Open Roles
          </Link>
        </div>

        {/* Loading State */}
        {loading && (
          <div className="flex justify-center items-center py-20">
            <Loader2 className="w-12 h-12 text-teal-500 animate-spin" />
          </div>
        )}

        {/* Empty State */}
        {!loading && applications.length === 0 && (
          <div className="bg-white rounded-xl shadow-soft border border-white/40 p-12 text-center">
            <FileText className="w-16 h-16 text-neutral-400 mx-auto mb-4" />
            <h3 className="text-2xl font-semibold text-navy-800 mb-2">
              No Applications Yet
            </h3>
            <p className="text-neutral-600 mb-6">
              Start your journey by submitting your first application
            </p>
            <Link
              to="/roles"
              className="inline-flex items-center px-6 py-3 bg-teal-500 text-white rounded-xl hover:bg-teal-600 transition-all duration-200 shadow-soft hover:shadow-soft font-medium"
              data-testid="empty-state-apply-button"
            >
              <Plus className="w-5 h-5 mr-2" />
              Explore Open Roles
            </Link>
          </div>
        )}

        {/* Applications Grid */}
        {!loading && applications.length > 0 && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {applications.map((app) => (
              <Link
                key={app._id}
                to={`/applications/${app._id}`}
                className="bg-white rounded-xl shadow-soft border border-white/40 hover:shadow-lg transition-all duration-300 p-6 block"
                data-testid="application-card"
              >
                {/* Header */}
                <div className="flex justify-between items-start mb-4">
                  <div>
                    <h3 className="text-lg font-semibold text-navy-800 mb-1">
                      {app.role?.title || 'Role'}
                    </h3>
                    <p className="text-sm text-neutral-600">
                      {app.role?.department} • {app.role?.location}
                    </p>
                  </div>
                  <span
                    className={`px-3 py-1 rounded-full text-xs font-medium border ${getStatusColor(
                      app.status
                    )}`}
                    data-testid="status-badge"
                  >
                    {app.status}
                  </span>
                </div>

                {/* Offer Letter Badge */}
                {app.status === 'Hired' && app.offer_letter && (
                  <div className="mb-4 p-3 bg-emerald-50 border border-emerald-200 rounded-xl">
                    <p className="text-sm text-emerald-800 font-medium">
                      🎉 Offer Letter Available - Click to view
                    </p>
                  </div>
                )}

                {/* Details */}
                <div className="space-y-2 text-sm text-neutral-600 mb-4">
                  <p>
                    <span className="font-medium text-navy-700">Applied:</span>{' '}
                    {formatDate(app.created_at)}
                  </p>
                  {app.interview?.scheduled_at && (
                    <p className="text-purple-600">
                      <span className="font-medium">Interview:</span>{' '}
                      {formatDate(app.interview.scheduled_at)}
                    </p>
                  )}
                </div>

                {/* Footer */}
                <div className="pt-4 border-t border-white/40">
                  <p className="text-sm text-teal-600 font-medium hover:text-teal-700">
                    View Details →
                  </p>
                </div>
              </Link>
            ))}
          </div>
        )}

        {/* Info Section */}
        {!loading && applications.length > 0 && (
          <div className="mt-12 bg-navy-900 rounded-xl p-8 text-white">
            <h3 className="text-2xl font-semibold mb-4">Need Help?</h3>
            <p className="text-neutral-300 mb-4">
              If you have any questions about your application or the hiring
              process, feel free to reach out to our team.
            </p>
            <a
              href="mailto:media@halfeatendonut.in"
              className="inline-flex items-center text-teal-400 hover:text-teal-300 font-medium transition-colors"
            >
              media@halfeatendonut.in →
            </a>
          </div>
        )}
      </main>
    </div>
  );
}
